import React, { useMemo, useEffect } from 'react';

// Lib
import { switcherIcon } from 'lib/thesaurus';
import { getDriverInstance } from 'lib/driver';
import { useAppDispatch } from 'lib/hooks/useDispatch';
import { useNavigate, useParams } from 'react-router-dom';

// Classnames
import clsx from 'clsx';

// Components
import Tree from 'rc-tree';
import NoData from 'components/NoData';
import Button from 'components/UiKit/Button';
import Loader from 'components/UiKit/Loader';
import Accordion from 'components/UiKit/Accordion';

// Actions
import * as actionsCreator from 'pages/Pfizer/reducer';

// Type
import { TreeNode } from 'pages/Thesaurus/Thesaurus';

interface ICategoriesView {
	uniqId: string;
	data: TreeNode[];
	loading: boolean;
	className?: string;
	selectedKeys: string[];
	expandedKeys: string[];
	allItems?: TreeNode[];
}

export const CategoriesView: React.FC<ICategoriesView> = (props) => {
	const {
		uniqId,
		loading,
		data,
		className,
		selectedKeys,
		expandedKeys,
		allItems
	} = props;

	const { slug = '' } = useParams();
	const navigate = useNavigate();
	const dispatch = useAppDispatch();

	const cdIdFallback: any = useMemo(() => {
		if (!allItems?.length) return null;
		const lower = slug?.toLowerCase();
		const node = allItems.find(
			(x) => x.extra?.name?.toLowerCase() === lower
		);

		return node || null;
	}, [allItems, slug]);

	useEffect(() => {
		const node = cdIdFallback;
		if (!node) return;

		dispatch(actionsCreator.updateSelectedKeys([node.value]));
		dispatch(actionsCreator.updateExpandedKeys(node.expandedKeys || []));
		dispatch(actionsCreator.changeTitle({
			categoryName: node.extra?.name || '',
			categoryDescription: node.extra?.description || ''
		}));

		dispatch(actionsCreator.getEbcCategoriesById(node.extra?.cdId));
	}, [cdIdFallback]);

	const handlerChangeToggleFilter = () => {
		dispatch(actionsCreator.changeToggleFilter());
	};

	const onSelect = (keys: any, info: any) => {
		const extra = info?.node?.extra;

		const id = extra?.cdId;
		const newSelectedKey = [info?.node?.value];
		const slug = encodeURIComponent(extra?.name || '');

		navigate(`/categorized-products/${slug}`, {
			replace: true,
			state: {
				cdId: id,
				name: extra?.name,
				description: extra?.description,
				selectedKeys: newSelectedKey,
				expandedKeys
			}
		});

		const drv = getDriverInstance();
		if (drv?.isActive()) {
			drv?.moveNext();
		}
	};

	const onExpand = (newExpandedKeys: any) => {
		dispatch(actionsCreator.updateExpandedKeys(newExpandedKeys));
	};

	const cnWrapper = clsx('pfizer__tree mr5 relative', className);

	const renderLoader = () => (
		<div className="pfizer__loader relative mb20 mt20">
			<Loader type="circle" color="dark" />
		</div>
	);

	const renderTree = () => (
		<Tree
			virtual
			selectable
			height={600}
			treeData={data}
			itemHeight={25}
			className="tree"
			// expandAction="click"
			switcherIcon={switcherIcon}

			onSelect={onSelect}
			selectedKeys={selectedKeys}

			onExpand={onExpand}
			expandedKeys={expandedKeys}
		/>
	);

	const renderNoData = () => (
		<div
			style={{ height: '5rem' }}
			className="flex flex-ai-center flex-jc-center mt30 mb30"
		>
			<NoData text="Nothing was found" />
		</div>
	);

	return (
		<div className={cnWrapper} id={uniqId || ''}>
			<div className="flex flex-ai-center flex-jc-between">
				<h4 className="pfizer__filter-title mb5">
					{/*Filters*/}
				</h4>
				<Button
					isSvg
					variable="icon"
					icon="crossSvg"
					title="Close filters"
					onPress={handlerChangeToggleFilter}
					className="pfizer__filter-close display-block-mobile"
				/>
			</div>
			<Accordion
				isOpen
				title="Categories"
				classNameTitle="mb5 mt5"
			>
				<>
					{loading
						? renderLoader()
						: data.length
							? renderTree()
							: renderNoData()
					}
				</>
			</Accordion>
		</div>
	);
};

export default CategoriesView;
